import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  useGetDocumentToFillDataMutation,
  useUploadFinanceDocumentMutation,
  useUploadFinanceDocumentsMutation,
} from "../../../../store/Company/companyApiSlice";
import { uploadFilePath } from "../../../../constants/s3UploadPath";
import { uploadFileToS3 } from "../../../../utils/uploadFileToS3";
import { CommonToast } from "../../../common/Toast";
import {
  MANUAL_FIELD_KEYS,
  financialYears,
  hasAnyFigure,
  isLastOfWindow,
  isSaveStage,
  manualPayload,
  manualYears,
  stepBack,
} from "./financialSetupFields";

const blankYear = () =>
  MANUAL_FIELD_KEYS.reduce((acc, key) => ({ ...acc, [key]: "" }), {});

/* Manual entry — figures for one year at a time. */
export const useManualFinancials = ({ onDone } = {}) => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [getDocumentToFillData] = useGetDocumentToFillDataMutation();
  const [uploadFinanceDocument] = useUploadFinanceDocumentMutation();
  const [uploadFinanceDocuments] = useUploadFinanceDocumentsMutation();

  /* Transcribed: the form walks the years newest-first, one at a time, and
   * `hideLastYear` decides WHICH five of the six are on offer — skipping the
   * current year trades it for one more year of history.
   */
  const [hideLastYear, setHideLastYear] = useState(true);
  const years = manualYears(hideLastYear);
  const [year, setYear] = useState(() => financialYears()[0]);
  /* { [year]: { total_revenue: "", … } } — every year the viewer has touched
     or the API already holds. */
  const [byYear, setByYear] = useState({});
  const [documents, setDocuments] = useState({});
  /* Every year the viewer has pressed Next or Save on, in that order. */
  const [committed, setCommitted] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  /* Transcribed: the figures already on record seed the form, under
     `existing` on each year's row. */
  const load = useCallback(async () => {
    if (!companyID) return;
    try {
      const res = await getDocumentToFillData({ companyId: companyID, token });
      const rows = res?.data?.data || [];

      const seededFigures = {};
      const seededDocs = {};
      rows.forEach((row) => {
        const rowYear = parseInt(String(row?.yearID || "").split("-")[0], 10);
        if (Number.isNaN(rowYear)) return;
        if (row?.document) seededDocs[rowYear] = row.document;
        if (row?.existing) {
          seededFigures[rowYear] = MANUAL_FIELD_KEYS.reduce(
            (acc, key) => ({ ...acc, [key]: row.existing[key] ?? "" }),
            {}
          );
        }
      });
      setByYear(seededFigures);
      setDocuments(seededDocs);
    } catch {
      /* The live call is unguarded, so a failure became an unhandled
         rejection. An empty form is the right fallback. */
      setByYear({});
      setDocuments({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID, token]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID]);

  const values = byYear[year] || blankYear();

  const setField = (key, value) => {
    setByYear((prev) => ({
      ...prev,
      [year]: { ...(prev[year] || blankYear()), [key]: value },
    }));
  };

  /* The optional supporting document for the year on screen. */
  const attachDocument = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await uploadFileToS3(
        uploadFilePath?.auditedFinancial,
        file,
        token,
        companyID,
        "financial_document"
      );
      if (uploaded?.success === false) {
        /* uploadFileToS3 reports its own reason via a toast. */
        return;
      }
      setDocuments((prev) => ({ ...prev, [year]: uploaded?.url }));
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = () => {
    setDocuments((prev) => {
      const next = { ...prev };
      delete next[year];
      return next;
    });
  };

  /* Transcribed: skipping the current year DELETES anything entered for it,
     which is what the live screen's tooltip warns about before you do it. */
  useEffect(() => {
    if (hideLastYear) return;
    const current = financialYears()[0];
    setByYear((prev) => {
      if (!(current in prev)) return prev;
      const next = { ...prev };
      delete next[current];
      return next;
    });
  }, [hideLastYear]);

  /* Jumping straight to a year from the strip — F-108. */
  const selectYear = (nextYear) => {
    if (nextYear === year) return;
    if (!years.includes(nextYear)) return;
    commitYear(year);
    setYear(nextYear);
  };

  /* The left control. On the newest year it SKIPS — dropping this year and
     opening the oldest instead; anywhere else it steps to a newer year. */
  const back = () => {
    const nextState = stepBack(year, hideLastYear);
    setYear(nextState.year);
    setHideLastYear(nextState.hideLastYear);
  };

  /* Records the year on screen, as the live `handleNavigation` does before it
     decides anything else. */
  const commitYear = (y) =>
    setCommitted((prev) => (prev.includes(y) ? prev : [...prev, y]));
  const commit = () => commitYear(year);

  /* Transcribed: the first two years only RECORD and step on — no request is made until the third. */
  const next = () => {
    commit();
    setYear(year - 1);
    return true;
  };

  const save = async () => {
    commit();
    setBusy(true);

    try {
      /* Every year walked through, not just the one on screen and not only the
         ones with figures — the live version accumulates them in `dataPayload`
         and sends the lot, nulls included. */
      const walked = committed.includes(year) ? committed : [...committed, year];
      const rows = walked.map((y) => manualPayload(y, byYear[y] || blankYear()));

      const figures = await uploadFinanceDocument({
        data: rows,
        company_id: companyID,
        token,
      });

      const docs = Object.keys(documents).map((y) => ({
        year: Number(y),
        document: documents[y],
      }));
      const uploaded = await uploadFinanceDocuments({
        data: docs,
        token,
        companyId: companyID,
        location: "fill",
      });

      /* Transcribed success test: both status codes, 201 and 200. */
      if (
        figures?.data?.statusCode === 201 &&
        uploaded?.data?.statusCode === 200
      ) {
        CommonToast.success(figures?.data?.message || "Financial figures saved.");
        /* Transcribed: a save on the LAST year of the window leaves the
           section; any other save steps to the next older year and stays. */
        if (isLastOfWindow(year, hideLastYear)) onDone?.();
        else setYear(year - 1);
        return true;
      }

      CommonToast.error(
        figures?.error?.data?.message ||
          uploaded?.error?.data?.message ||
          "Couldn't save these figures. Please try again."
      );
      return false;
    } catch (err) {
      /* The live handler's catch is empty, so a rejected save said nothing at
         all. */
      CommonToast.error(
        err?.data?.detail ||
          err?.data?.message ||
          err?.message ||
          "Couldn't save these figures. Please try again."
      );
      return false;
    } finally {
      setBusy(false);
    }
  };

  return {
    years,
    year,
    setYear,
    /* What the strip calls. See the note on selectYear. */
    selectYear,
    /* Which control the right-hand button is, and what the left one says. */
    atSaveStage: isSaveStage(year, hideLastYear),
    onNewestYear: year === financialYears()[0],
    hideLastYear,
    back,
    next,
    values,
    setField,
    document: documents[year] || "",
    attachDocument,
    removeDocument,
    uploading,
    busy,
    save,
    /* Which years already carry something, so the tab strip can say so. */
    filledYears: Object.keys(byYear)
      .filter((y) => hasAnyFigure(byYear[y]))
      .map(Number),
  };
};

export default useManualFinancials;
